import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Pencil, Play, UserCheck, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { Avatar } from "../components/Avatar";
import { PlaylistCard, togglePlaylistLike } from "../components/PlaylistCard";
import { SongCard } from "../components/SongCard";
import { usePlayer } from "../context/PlayerContext";
import { errMsg, http } from "../lib/api";

const Stats = ({ stats }) => (
  <div data-testid="creator-stats" className="flex flex-wrap gap-x-8 gap-y-4 font-mono">
    {[["sekėjų", stats.followers], ["dainų", stats.songs], ["perklausų", stats.plays], ["patiktukų", stats.likes], ["grojaraščių", stats.playlists]].map(([l, v]) => (
      <div key={l}><p className="text-2xl font-display">{v}</p><p className="text-[11px] uppercase tracking-wider text-zinc-500">{l}</p></div>
    ))}
  </div>
);

const FollowButton = ({ c, setC }) => {
  const toggle = async () => {
    try {
      const { data } = await http.post(`/creators/${c.user_id}/follow`);
      setC((x) => ({ ...x, is_following: data.following, stats: { ...x.stats, followers: data.followers } }));
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <button data-testid="creator-follow-button" onClick={toggle}
      className={`flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold transition-colors ${c.is_following ? "border border-[var(--c-orange)] text-[var(--c-orange)] hover:bg-[var(--c-orange)]/10" : "bg-white text-black hover:bg-zinc-200"}`}>
      {c.is_following ? <UserCheck className="w-4 h-4" /> : <UserPlus className="w-4 h-4" />}{c.is_following ? "Sekama" : "Sekti"}
    </button>
  );
};

const CreatorHeader = ({ c, setC }) => {
  const p = usePlayer();
  return (
    <header className="flex flex-col lg:flex-row lg:items-end justify-between gap-8">
      <div className="flex flex-col sm:flex-row sm:items-end gap-6 min-w-0">
        <Avatar user={c} className="w-32 h-32 text-5xl ring-4 ring-[var(--c-orange)]/30" testId="creator-avatar" />
        <div className="min-w-0">
          <p className="text-xs uppercase tracking-[0.2em] text-[var(--c-orange)] font-semibold">Kūrėjas</p>
          <h1 data-testid="creator-name" className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold mt-2 break-words">{c.name}</h1>
          {c.bio && <p data-testid="creator-bio" className="text-zinc-300 mt-3 max-w-xl whitespace-pre-line">{c.bio}</p>}
          <p className="text-xs text-zinc-500 mt-2">Kuria nuo {new Date(c.created_at).toLocaleDateString("lt-LT")}</p>
          <div className="flex flex-wrap gap-3 mt-5">
            {c.songs.length > 0 && (
              <button data-testid="creator-play-all" onClick={() => p.playAll(c.songs)}
                className="flex items-center gap-2 px-7 py-3 rounded-full bg-[var(--c-orange)] text-white font-semibold glow-orange hover:bg-[#ff7a26] transition-colors">
                <Play className="w-4 h-4" fill="currentColor" />Groti visas
              </button>
            )}
            {!c.is_me && <FollowButton c={c} setC={setC} />}
            {c.is_me && (
              <Link to="/profilis" data-testid="creator-edit-profile" className="flex items-center gap-2 px-5 py-3 rounded-full border border-[var(--c-line)] text-sm hover:border-white transition-colors">
                <Pencil className="w-4 h-4" />Redaguoti profilį
              </Link>
            )}
          </div>
        </div>
      </div>
      <Stats stats={c.stats} />
    </header>
  );
};

const Section = ({ title, testId, children }) => (
  <section data-testid={testId} className="space-y-5">
    <h2 className="font-display text-base md:text-lg font-bold">{title}</h2>
    {children}
  </section>
);

export default function Creator() {
  const { id } = useParams();
  const [c, setC] = useState(null);
  const [missing, setMissing] = useState(false);
  useEffect(() => { http.get(`/creators/${id}`).then((r) => setC(r.data)).catch(() => setMissing(true)); }, [id]);
  const setLists = (fn) => setC((x) => ({ ...x, playlists: fn(x.playlists) }));

  if (missing) return <p data-testid="creator-not-found" className="text-zinc-400">Kūrėjas nerastas. <Link to="/atrasti" className="text-[var(--c-orange)]">Grįžti</Link></p>;
  if (!c) return <div className="h-64 rounded-3xl shimmer" />;

  return (
    <div className="space-y-14 rise">
      <CreatorHeader c={c} setC={setC} />
      <Section title="Dainos" testId="creator-songs">
        {c.songs.length === 0 && <p data-testid="creator-songs-empty" className="text-sm text-zinc-500">Viešų dainų dar nėra.</p>}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 2xl:grid-cols-5 gap-x-5 gap-y-8">
          {c.songs.map((s, i) => <SongCard key={s.id} song={s} list={c.songs} index={i} />)}
        </div>
      </Section>
      <Section title="Grojaraščiai" testId="creator-playlists">
        {c.playlists.length === 0 && <p data-testid="creator-playlists-empty" className="text-sm text-zinc-500">Viešų grojaraščių dar nėra.</p>}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 2xl:grid-cols-6 gap-x-5 gap-y-8">
          {c.playlists.map((pl, i) => <PlaylistCard key={pl.id} pl={pl} index={i} onLike={(p) => togglePlaylistLike(p, setLists)} />)}
        </div>
      </Section>
    </div>
  );
}
